import { useRef, useEffect, useCallback } from "react";

interface PixelFaceCanvasProps {
  skinTone: string;
  expression: number; // 0-100, 50 = neutral
  showFullBody?: boolean; // Si es true, muestra el cuerpo completo
  containerHeight?: number; // Altura dinámica del contenedor
}

const GRID = 128;

// Helper: parse hex color to RGB
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

// Helper: darken/lighten a color
function adjustColor(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const clamp = (v: number) => Math.max(0, Math.min(255, v));
  return `rgb(${clamp(r + amount)},${clamp(g + amount)},${clamp(b + amount)})`;
}

// Helper: blend two colors
function blendColor(hex1: string, hex2: string, t: number): string {
  const [r1, g1, b1] = hexToRgb(hex1);
  const r2 = parseInt(hex2.replace("#", "").substring(0, 2), 16);
  const g2x = parseInt(hex2.replace("#", "").substring(2, 4), 16);
  const b2 = parseInt(hex2.replace("#", "").substring(4, 6), 16);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2x - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
}

// Draw a filled ellipse of pixels
function fillEllipse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  color: string
) {
  ctx.fillStyle = color;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const dx = (x - cx) / rx;
      const dy = (y - cy) / ry;
      if (dx * dx + dy * dy <= 1) {
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }
}

// Draw a filled rectangle of pixels
function fillRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string
) {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

export default function PixelFaceCanvas({ skinTone, expression, showFullBody = false, containerHeight }: PixelFaceCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

  // v: -1 (muy triste) .. 0 (neutral) .. +1 (muy feliz)
  const v = clamp((expression - 50) / 50, -1, 1);
  const happy = Math.max(0, v);
  const sad = Math.max(0, -v);
  const intensity = Math.abs(v);

  const drawFace = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Clear
      ctx.clearRect(0, 0, GRID, GRID);

      const eyeColor = "#1a1a2e";
      const eyeWhite = "#FFFFFF";
      const noseColor = adjustColor(skinTone, -20);

      // ===== NOSE (constant shape, subtle) =====
      fillEllipse(ctx, 64, 78, 4, 3, noseColor);
      // Nostrils
      fillRect(ctx, 60, 79, 2, 1, adjustColor(skinTone, -40));
      fillRect(ctx, 66, 79, 2, 1, adjustColor(skinTone, -40));

      // ===== EYEBROWS (más humanas) =====
      const browColor = "#2C1810";
      const browLen = 18;

      // y baja/sube según expresión (Y crece hacia abajo)
      const browY = 46 - Math.round(3 * sad) + Math.round(1 * happy);

      // "Pinch" típico de tristeza/preocupación: interno arriba, externo abajo
      const innerLift = -Math.round(4 * sad) + Math.round(1 * happy); // negativo = sube
      const outerLift =  Math.round(3 * sad) - Math.round(2 * happy);

      // arco (felicidad): sube el centro un poco
      const arch = Math.round(2 * happy);

      // grosor más natural (no siempre igual)
      const browThicknessBase = 3 + Math.round(0.8 * intensity);

      // micro-asimetría leve: hace que no parezca espejo perfecto
      const asym = Math.round(1 * happy) - Math.round(1 * sad * 0.5);

      // Left brow
      for (let i = 0; i < browLen; i++) {
        const p = i / (browLen - 1);          // 0..1
        const mid = 1 - Math.abs(p * 2 - 1);  // 0 en puntas, 1 en centro
        const archOffset = -Math.round(arch * mid);

        const yOffset = Math.round(innerLift * (1 - p) + outerLift * p) + archOffset;

        // pequeño "quiebre" de pelitos para que no sea una barra perfecta
        if (sad > 0.55 && i === Math.floor(browLen * 0.35)) continue;

        const th = browThicknessBase - (mid > 0.7 ? 0 : 1);
        fillRect(ctx, 36 + i, browY + yOffset, 1, Math.max(2, th), browColor);
      }

      // Right brow (espejo, pero con asimetría sutil)
      for (let i = 0; i < browLen; i++) {
        const p = i / (browLen - 1);
        const mid = 1 - Math.abs(p * 2 - 1);
        const archOffset = -Math.round(arch * mid);

        const yOffset = Math.round(outerLift * (1 - p) + innerLift * p) + archOffset + asym;

        if (sad > 0.55 && i === Math.floor(browLen * 0.65)) continue;

        const th = browThicknessBase - (mid > 0.7 ? 0 : 1);
        fillRect(ctx, 74 + i, browY + yOffset, 1, Math.max(2, th), browColor);
      }

      // Pequeño pliegue glabelar (entre cejas) cuando hay tristeza/preocupación
      if (sad > 0.55) {
        const crease = adjustColor(skinTone, -18);
        fillRect(ctx, 63, 50, 1, 3, crease);
        fillRect(ctx, 65, 50, 1, 3, crease);
      }

      // ===== EYES =====
      // Constant shape (oval), but pupil position and eyelid change with expression
      const eyeY = 62;
      const leftEyeX = 47;
      const rightEyeX = 81;
      const eyeRx = 9;
      // ojos se achican con sonrisa (squint) y también un poco con tristeza (caída)
      const eyeRy = Math.max(4, Math.round(7 - 2 * happy - 1 * sad));

      // Eye whites
      fillEllipse(ctx, leftEyeX, eyeY, eyeRx, eyeRy, eyeWhite);
      fillEllipse(ctx, rightEyeX, eyeY, eyeRx, eyeRy, eyeWhite);

      // Eye outline
      const outlineColor = "#1a1a2e";
      for (let angle = 0; angle < 360; angle += 1) {
        const rad = (angle * Math.PI) / 180;
        const ox = Math.round(leftEyeX + Math.cos(rad) * eyeRx);
        const oy = Math.round(eyeY + Math.sin(rad) * eyeRy);
        fillRect(ctx, ox, oy, 1, 1, outlineColor);
        const ox2 = Math.round(rightEyeX + Math.cos(rad) * eyeRx);
        fillRect(ctx, ox2, oy, 1, 1, outlineColor);
      }

      // Pupils - shift slightly based on expression
      // "puppy eyes" leve: triste mira un pelín hacia arriba; feliz baja/centra
      const pupilOffsetY = Math.round(-1 * sad + 0.5 * happy);
      const pupilOffsetX = Math.round(0.5 * happy); // micro-movimiento lateral
      const pupilSize = 4;

      // Iris
      fillEllipse(ctx, leftEyeX + pupilOffsetX, eyeY + pupilOffsetY, pupilSize + 1, pupilSize + 1, "#3a2a1a");
      fillEllipse(ctx, rightEyeX + pupilOffsetX, eyeY + pupilOffsetY, pupilSize + 1, pupilSize + 1, "#3a2a1a");

      // Pupil
      fillEllipse(ctx, leftEyeX + pupilOffsetX, eyeY + pupilOffsetY, pupilSize, pupilSize, eyeColor);
      fillEllipse(ctx, rightEyeX + pupilOffsetX, eyeY + pupilOffsetY, pupilSize, pupilSize, eyeColor);

      // Eye shine
      fillRect(ctx, leftEyeX + pupilOffsetX - 2, eyeY + pupilOffsetY - 2, 2, 2, "#FFFFFF");
      fillRect(ctx, rightEyeX + pupilOffsetX - 2, eyeY + pupilOffsetY - 2, 2, 2, "#FFFFFF");

      // ===== EYELIDS (humanizados) =====
      const upperLid = Math.round(1 + 4 * sad + 2 * happy); // tapa arriba
      const lowerLid = Math.round(0 + 3 * happy);           // squint por mejillas

      if (upperLid > 0) {
        fillEllipse(ctx, leftEyeX,  eyeY - eyeRy + upperLid, 9, upperLid, skinTone);
        fillEllipse(ctx, rightEyeX, eyeY - eyeRy + upperLid, 9, upperLid, skinTone);
      }

      if (lowerLid > 0) {
        fillEllipse(ctx, leftEyeX,  eyeY + eyeRy - lowerLid, 9, lowerLid, skinTone);
        fillEllipse(ctx, rightEyeX, eyeY + eyeRy - lowerLid, 9, lowerLid, skinTone);
      }

      // Línea de párpado inferior (muy sutil, solo en sonrisa media+)
      if (happy > 0.35) {
        const lidLine = adjustColor(skinTone, -22);
        for (let dx = -6; dx <= 6; dx++) {
          fillRect(ctx, leftEyeX + dx,  eyeY + eyeRy - 1, 1, 1, lidLine);
          fillRect(ctx, rightEyeX + dx, eyeY + eyeRy - 1, 1, 1, lidLine);
        }
      }

      // Lagrimal (da "humano" instantáneo)
      const tearDuct = blendColor(skinTone, "#ff8fa3", 0.25);
      fillRect(ctx, leftEyeX + 6,  eyeY + 2, 1, 1, tearDuct);
      fillRect(ctx, rightEyeX + 6, eyeY + 2, 1, 1, tearDuct);

      // Pestañas mínimas cuando sonríe
      if (happy > 0.55) {
        fillRect(ctx, leftEyeX - 7,  eyeY - eyeRy + 1, 2, 1, outlineColor);
        fillRect(ctx, rightEyeX - 7, eyeY - eyeRy + 1, 2, 1, outlineColor);
      }

      // ===== MOUTH (suave, estilo referencia; t=1 es sonrisa pequeña) =====
      const mouthY = 92;
      const mouthCenterX = 64;

      // easing para que el cambio sea más orgánico (no lineal)
      const ease = (x: number) => x * x * (3 - 2 * x);

      // Curva máxima MUCHO más baja:
      // + = sonrisa (centro cae), - = tristeza (centro sube)
      const mouthCurve = Math.round(4 * ease(happy) - 4 * ease(sad)); // -4..+4

      // Apertura SOLO en sonrisa muy alta (tu imagen es apertura pequeña)
      const mouthOpen = Math.round(3 * Math.pow(ease(Math.max(0, happy - 0.55) / 0.45), 1.2)); // 0..3

      // Anchura más contenida
      const mouthWidth = 18 + Math.round(2 * ease(happy)) - Math.round(1 * ease(sad)); // ~17..20

      const mouthColor = "#1a1a2e";
      const lipColor = "#c0616b";
      const innerMouth = "#2a0a0a";

      // Dibuja una U suave: curva alta en el centro, 0 en los extremos
      for (let i = -mouthWidth / 2; i <= mouthWidth / 2; i++) {
        const p = i / (mouthWidth / 2);            // -1..1
        const arc = mouthCurve * (1 - p * p);      // U (sonrisa) / ∩ (tristeza)
        const px = Math.round(mouthCenterX + i);
        const py = Math.round(mouthY + arc);

        // línea de labio (más fina para que no se vea pesada)
        fillRect(ctx, px, py, 1, 1, mouthColor);

        // comisuras (1px extra para que se vea humano, no "linea perfecta")
        if (Math.abs(i) >= mouthWidth / 2 - 1) {
          // si sonríe: comisura un pixel arriba; si triste: un pixel abajo
          fillRect(ctx, px, py + (mouthCurve >= 0 ? -1 : 1), 1, 1, mouthColor);
        }

        // interior (apertura pequeña)
        if (mouthOpen > 0) {
          const openAmount = Math.round(mouthOpen * (1 - p * p)); // más al centro
          if (openAmount > 0) {
            fillRect(ctx, px, py + 1, 1, openAmount, innerMouth);
            fillRect(ctx, px, py + 1 + openAmount, 1, 1, lipColor); // labio inferior
          }
        }

        // toque de color labial arriba cuando hay emoción (muy sutil)
        if (Math.abs(mouthCurve) >= 2 && Math.abs(i) < mouthWidth / 2 - 2) {
          fillRect(ctx, px, py - 1, 1, 1, lipColor);
        }
      }

      // ===== CHEEKS (blush) =====
      // More blush when very happy or very sad
      const blushIntensity = intensity; // 0 at neutral, 1 at extremes
      if (blushIntensity > 0.3) {
        const blushColor = blendColor(skinTone, "#FF6B6B", blushIntensity * 0.35);
        fillEllipse(ctx, 36, 82, 7, 4, blushColor);
        fillEllipse(ctx, 92, 82, 7, 4, blushColor);
      }

      // ===== FOREHEAD WRINKLES (when worried/sad) =====
      if (sad > 0.3) {
        const wrinkleAlpha = sad;
        const wrinkleColor = adjustColor(skinTone, Math.round(-15 * wrinkleAlpha));
        for (let row = 0; row < 2; row++) {
          const wy = 36 + row * 4;
          for (let i = -12; i <= 12; i++) {
            const wave = Math.round(Math.sin(i * 0.3) * 1);
            fillRect(ctx, 64 + i, wy + wave, 1, 1, wrinkleColor);
          }
        }
      }

      // ===== Under-eye area when sad =====
      if (sad > 0.35) {
        const bagIntensity = sad;
        const bagColor = adjustColor(skinTone, Math.round(-12 * bagIntensity));
        fillEllipse(ctx, leftEyeX, eyeY + eyeRy + 2, 6, 2, bagColor);
        fillEllipse(ctx, rightEyeX, eyeY + eyeRy + 2, 6, 2, bagColor);
      }

      // ===== SWEAT DROP (very sad) =====
      if (sad > 0.85) {
        fillEllipse(ctx, 26, 52, 2, 3, "#87CEEB");
        fillRect(ctx, 25, 48, 2, 3, "#87CEEB");
      }

      // ===== SPARKLES (very happy) =====
      if (happy > 0.9) {
        const sparkle = "#FFD700";
        // Left sparkle
        fillRect(ctx, 18, 40, 3, 1, sparkle);
        fillRect(ctx, 19, 39, 1, 3, sparkle);
        // Right sparkle
        fillRect(ctx, 107, 42, 3, 1, sparkle);
        fillRect(ctx, 108, 41, 1, 3, sparkle);
      }
    },
    [skinTone, v, happy, sad, intensity]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    drawFace(ctx);
  }, [drawFace]);

  // Calcular la altura del contenedor para mostrar más o menos del cuerpo
  const displayHeight = containerHeight || 192;

  return (
    <div className="relative flex flex-col items-center">
      {/* Face canvas */}
      <div 
        className="relative w-48 overflow-visible"
        style={{ height: `${displayHeight}px` }}
      >
        {/* Base face PNG - behind everything */}
        <img 
          src="https://qwlfbxcbghlnoxiijqum.supabase.co/storage/v1/object/public/personajes/caras/cara.png"
          alt="Face base"
          className="absolute left-0 top-0 w-48 h-48 z-0"
          style={{ imageRendering: "pixelated" }}
        />
        
        {/* Animated facial features on top */}
        <canvas
          ref={canvasRef}
          width={GRID}
          height={GRID}
          className="absolute left-0 top-0 w-48 h-48 z-10"
          style={{ imageRendering: "pixelated" }}
        />

        {/* Body PNG - below hair, above face - positioned relative to face, not container */}
        <img 
          src="https://qwlfbxcbghlnoxiijqum.supabase.co/storage/v1/object/public/personajes/ropa/cuerpo.png"
          alt="Body"
          className="absolute left-0 w-48 z-20"
          style={{ 
            imageRendering: "pixelated", 
            top: "176px",
            height: "auto",
            maxHeight: "none"
          }}
        />

        {/* Hair PNG - on top of everything */}
        <img 
          src="https://qwlfbxcbghlnoxiijqum.supabase.co/storage/v1/object/public/personajes/cabello/cabello.png"
          alt="Hair"
          className="absolute left-0 top-0 w-48 h-48 z-30"
          style={{ imageRendering: "pixelated" }}
        />
      </div>
    </div>
  );
}
